import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Box, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens, displayFont, radius } from "../theme";
import { pressable } from "./pressableProps";

/**
 * An overlay, to the record book's spec.
 *
 * The scrim is dark plum rather than black: a neutral wash over a warm page
 * greys it, and the product's own dark is what the sidebar already uses.
 *
 * It aligns to the top rather than centring, and scrolls the scrim rather than
 * the panel — a centred dialog taller than the viewport has nowhere to go, and
 * a panel with its own scrollbar hides its footer, which is where the button
 * that finishes the job lives.
 *
 * Rendered through a portal into #portal-container, a sibling of #root, rather
 * than where it is written in the tree. `position: fixed` resolves against the
 * viewport only when no ancestor is a containing block for it, and the content
 * column is one: frameLayout's scrollColumnSx is itself position:fixed with
 * overflow auto/hidden, so a dialog opened from any screen was laid out and
 * clipped inside that column. It began at the sidebar's edge, left the top bar
 * undimmed, and the scrim covered the content rather than the page — which is
 * not a modal, it is a panel wearing one's clothes. Nine screens use this.
 *
 * Deliberately not the .portal-component wrapper used elsewhere: that class is
 * position:absolute with z-index 1, which would open a stacking context and
 * trap this dialog's z-index 60 underneath the sidebar's 10.
 */
const SCRIM = "rgba(94, 37, 64, 0.32)";

export const ModalHead = ({ title, subtitle, onClose }) => (
  <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ p: "24px 28px 20px", borderBottom: `1px solid ${tokens.border}` }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ fontFamily: displayFont, fontSize: "1.3125rem", fontWeight: 700, color: tokens.text, mb: "3px" }}>
        {title}
      </Box>
      {subtitle && <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>{subtitle}</Box>}
    </Box>
    {onClose && (
      <Box
        {...pressable(onClose)}
        aria-label="Close"
        sx={{
          width: "2rem", height: "2rem", borderRadius: "8px", flex: "none",
          border: `1px solid ${tokens.borderStrong}`, color: tokens.textMuted,
          display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          "&:hover": { background: tokens.surfaceSubtle },
        }}
      >
        <CloseIcon sx={{ fontSize: "1.125rem" }} />
      </Box>
    )}
  </Stack>
);

/**
 * The footer: a line of explanation on the left, the actions on the right.
 * It sits on the page blush so it reads as a ledge under the content rather
 * than as more of the form.
 */
export const ModalFoot = ({ note, children }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ p: "18px 28px", borderTop: `1px solid ${tokens.border}`, background: tokens.surface }}
  >
    <Box sx={{ flex: 1, fontSize: "0.8125rem", color: tokens.textMuted }}>{note}</Box>
    {children}
  </Stack>
);

export const ModalBody = ({ children, sx }) => (
  <Box sx={{ p: "24px 28px", ...sx }}>{children}</Box>
);

const Modal = ({ open, onClose, width = "44rem", children, labelledBy }) => {
  // Escape closes, because a scrim you can only leave by finding the small
  // button in the corner is a trap on a keyboard.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    // The page behind must not scroll under the scrim.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const content = (
    <Box
      onClick={onClose}
      role="presentation"
      sx={{
        position: "fixed",
        inset: 0,
        background: SCRIM,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "56px 24px",
        overflowY: "auto",
        zIndex: 60,
      }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        // The panel is not the scrim: a click inside it must not close.
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: "100%",
          maxWidth: width,
          background: tokens.card,
          borderRadius: `${radius.card}px`,
          boxShadow: "0 32px 64px -20px rgba(38, 36, 29, 0.45)",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </Box>
  );

  // Falls back to rendering in place if the container is missing, which is
  // better than rendering nothing at all: a mispositioned dialog is a bug, an
  // absent one loses the user's work.
  const host = typeof document !== "undefined" && document.getElementById("portal-container");
  return host ? createPortal(content, host) : content;
};

export default Modal;
